import React from "react";
import { Box, Typography } from "@mui/material";
import moment from "moment";

const MAIN_FONT = "Noto Sans TC";

const THEME = {
    time: {
        color: "#C8A020",
        textSize: "10px",
    },
    schoolName: {
        color: "#24bbdd",
        textSize: "15px",
    },
    playerName: {
        color: "#24bbdd",
        textSize: "15px",
    },
    actionText: {
        color: "#aaa",
        textSize: "15px",
    },
    skillName: {
        color: "#E040FB",
        textSize: "15px",
    },
    gamepadIcon: {
        textSize: "15px",
    },
}

function getTimeAgoText(timestamp) {
    // withing 1 days using fromNow
    // more than 1 day, show exact date time in format "YYYY-MM-DD HH:mm"
    const now = moment();
    const time = moment(timestamp);
    if (now.diff(time, "days") < 1) {
        return time.fromNow();
    } else {
        return time.format("YYYY-MM-DD HH:mm");
    }
}


/**
 * LatestPlayActivityCard
 *
 * Props:
 *   data         {{ id, member_id, display_name, org_name_zh, skill_code, created_at }}
 *   skillNameMap {{ [skill_code]: string }}
 */
export default function LatestPlayActivityCard({ data, skillNameMap = {} }) {
    if (!data) return null;

    const { display_name, org_name_zh, skill_code, created_at } = data;
    const skillName = skillNameMap[skill_code] ?? skill_code;
    const timeAgo = getTimeAgoText(created_at);

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                borderRadius: "10px",
                border: "1.5px solid rgba(140, 60, 255, 0.85)",
                background:
                    "linear-gradient(135deg, rgba(8, 15, 40, 0.92) 0%, rgba(12, 20, 55, 0.88) 100%)",
                boxShadow:
                    "0 0 18px rgba(130, 60, 255, 0.45), inset 0 0 30px rgba(0, 100, 200, 0.08)",
                px: 2.5,
                py: 1.5,
                overflow: "hidden",
                // Circuit-board corner accents via pseudo elements via overlay Box
            }}
        >
            {/* Corner accent — top-right */}
            <Box
                sx={{
                    position: "absolute",
                    top: 6,
                    right: 10,
                    display: "flex",
                    gap: "3px",
                    alignItems: "center",
                    opacity: 0.5,
                }}
            >
                {[16, 10, 22, 8, 14].map((w, i) => (
                    <Box
                        key={i}
                        sx={{
                            width: w,
                            height: 3,
                            background: "rgba(100, 180, 255, 0.7)",
                            borderRadius: 1,
                        }}
                    />
                ))}
            </Box>

            {/* Time */}
            <Typography
                sx={{
                    fontFamily: MAIN_FONT,
                    fontSize: THEME.time.textSize,
                    fontWeight: "700",
                    color: THEME.time.color,
                    mb: 0.5,
                    lineHeight: 1.2,
                }}
            >
                {timeAgo}
            </Typography>

            {/* Main activity line */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "6px",
                    lineHeight: 1.3,
                }}
            >
                {/* School name */}
                <Typography
                    sx={{
                        fontFamily: MAIN_FONT,
                        fontSize: THEME.schoolName.textSize,
                        fontWeight: "900",
                        color: THEME.schoolName.color,
                        textShadow: "0 0 10px rgba(255,255,255,0.3)",
                    }}
                >
                    {org_name_zh}
                </Typography>

                {/* Player name */}
                <Typography
                    sx={{
                        fontFamily: MAIN_FONT,
                        fontSize: THEME.playerName.textSize,
                        fontWeight: "900",
                        color: THEME.playerName.color,
                        textShadow: "0 0 10px rgba(255,255,255,0.3)",
                    }}
                >
                    {display_name}
                </Typography>

                {/* 正在玩 */}
                <Typography
                    sx={{
                        fontFamily: MAIN_FONT,
                        fontSize: THEME.actionText.textSize,
                        fontWeight: "500",
                        color: THEME.actionText.color,
                    }}
                >
                    正在玩
                </Typography>

                {/* Skill name */}
                <Typography
                    sx={{
                        fontFamily: MAIN_FONT,
                        fontSize: THEME.skillName.textSize,
                        fontWeight: "900",
                        color: THEME.skillName.color,
                        textShadow: THEME.skillName.textShadow,
                    }}
                >
                    {skillName}
                </Typography>

                {/* Gamepad icon */}
                <Typography
                    sx={{
                        fontSize: THEME.gamepadIcon.textSize,
                        lineHeight: 1,
                    }}
                >
                    🎮
                </Typography>
            </Box>
        </Box>
    );
}
